import { memo } from 'react';
import { sliceContent } from '@restworld/utils-common';
import { titleIconMap, colorMap, sizeMap, fontWeightMap } from './title.constants';
import { IconWrapper, TitleContent, TitleWrapper, standardVariantToHtmlElement, variantToHtmlElement } from './title.styled-components';
/**
 * A component that renders a title with an optional icon and suffix icon.
 *
 * @param {string | React.ReactNode} content - The content of the title. Can be a string or a React node.
 * @param {FontSize} fontSize - The font size of the title. Defaults to 'base'.
 * @param {TextColor} textColor - The color of the title. Defaults to 'black'.
 * @param {FontWeightType} fontWeight - The font weight of the title. Defaults to 'normal'.
 * @param {string} icon - The name of the icon to display before the title.
 * @param {boolean} isCenter - Whether to center the title. Defaults to false.
 * @param {string} suffixIcon - The name of the icon to display after the title.
 * @param {number} maxChars - The maximum number of characters to display in the title. If the content is longer than this, it will be truncated. Defaults to undefined.
 * @param {React.ReactNode} children - Additional content to display inside the title.
 * @param {boolean} isPointer - Whether the title should have a pointer cursor. Defaults to false.
 * @param {string} twStyle - Tailwind CSS styles to apply to the title.
 * @param {string} variant - The variant of the title. Defaults to undefined.
 */

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var Title = /*#__PURE__*/memo(function (_ref) {
  var content = _ref.content,
      _ref$fontSize = _ref.fontSize,
      fontSize = _ref$fontSize === void 0 ? 'base' : _ref$fontSize,
      _ref$textColor = _ref.textColor,
      textColor = _ref$textColor === void 0 ? 'black' : _ref$textColor,
      _ref$fontWeight = _ref.fontWeight,
      fontWeight = _ref$fontWeight === void 0 ? 'normal' : _ref$fontWeight,
      icon = _ref.icon,
      _ref$isCenter = _ref.isCenter,
      isCenter = _ref$isCenter === void 0 ? false : _ref$isCenter,
      suffixIcon = _ref.suffixIcon,
      maxChars = _ref.maxChars,
      children = _ref.children,
      isPointer = _ref.isPointer,
      twStyle = _ref.twStyle,
      variant = _ref.variant,
      wrapperStyle = _ref.wrapperStyle,
      desktopOnly = _ref.desktopOnly,
      mobileOnly = _ref.mobileOnly,
      align = _ref.align,
      standardVariant = _ref.standardVariant,
      testId = _ref.testId,
      customPrefixIcon = _ref.customPrefixIcon,
      customSuffixIcon = _ref.customSuffixIcon,
      as = _ref.as;
  return /*#__PURE__*/_jsxs(TitleWrapper, {
    fontSize: fontSize,
    textColor: textColor,
    isCenter: isCenter,
    wrapperStyle: wrapperStyle,
    mobileOnly: mobileOnly,
    desktopOnly: desktopOnly,
    as: as || standardVariantToHtmlElement(standardVariant) || variantToHtmlElement(variant),
    "data-cy": testId,
    children: [icon && /*#__PURE__*/_jsx(IconWrapper, {
      children: titleIconMap[icon]
    }), customPrefixIcon && /*#__PURE__*/_jsx(IconWrapper, {
      children: customPrefixIcon
    }), /*#__PURE__*/_jsx(TitleContent, {
      fontWeight: fontWeight,
      isPointer: isPointer,
      twStyle: twStyle,
      variant: variant,
      align: align,
      standardVariant: standardVariant,
      "data-cy": "title-content",
      children: children ? children : !!content && typeof content === 'string' && maxChars ? sliceContent(content, maxChars) : content
    }), suffixIcon ? /*#__PURE__*/_jsx(IconWrapper, {
      children: titleIconMap[suffixIcon]
    }) : null, customSuffixIcon ? /*#__PURE__*/_jsx(IconWrapper, {
      children: customSuffixIcon
    }) : null]
  });
});
export { titleIconMap, sizeMap, colorMap, fontWeightMap };
export default Title;